import { AppointmentService } from './../../shared/services/appointment.service';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import * as fromAdapter from './bdcdashboard.adapter';
import * as fromActions from './bdcdashboard.actions';
import { IAppointment, IResponse, IVehicle } from '@signal/asp-data-commons';
import { AlertService } from '../../shared/services/alert.service';
import { LoadPendingAppointmentCount } from '../service-appointment/service-appointment.actions';
import { ServiceAppointmentState } from '../service-appointment/service-appointment.reducer';

@Injectable()
export class DashboardEffects {
  somethingWentWrongPleaseTryAgain =  'otherTexts.somethingWentWrongPleaseTryAgain';

  constructor(
    private readonly actions$: Actions,
    private readonly appointmentService: AppointmentService,
    private readonly alertService: AlertService,
    private readonly serviceAppointmentStore: Store<ServiceAppointmentState>
  ) {
  }

  
  loadUserVehicles: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadUserVehicles>(
      fromActions.BdcDashboardActionTypes.LOAD_USER_VEHICLES
    ),
    mergeMap((action: fromActions.LoadUserVehicles) =>
      this.appointmentService.getVehicles(action.payload.dealerCode, action.payload.customerId).pipe(
        map((vehicles: IVehicle[]) => new fromActions.LoadUserVehiclesSuccess({ vehicles })),
        catchError(err => of(new fromActions.LoadUserVehiclesFail({ error: err })))
      )
    )
  ));

  
  loadAppointments: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadAppointments>(
      fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS
    ),
    mergeMap((action: fromActions.LoadAppointments) =>
      this.appointmentService.search(action.payload.queryText).pipe(
        switchMap((appointments: IResponse<IAppointment[]>) => fromAdapter.toAppointmentsProjector(appointments)),
        map((appointments: IAppointment[]) => new fromActions.LoadAppointmentsSuccess({ appointments })),
        catchError(err => of(new fromActions.LoadAppointmentsFail({ error: err })))
      )
    )
  ));
  
  loadAppointmentsforvin: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadAppointmentsForVin>(
      fromActions.BdcDashboardActionTypes.LOAD_APPOINTMENTS_FOR_VIN
    ),
    mergeMap((action: fromActions.LoadAppointmentsForVin) =>
      this.appointmentService.search(action.payload.queryText).pipe(
        switchMap((appointments: IResponse<IAppointment[]>) => fromAdapter.toAppointmentsProjector(appointments)),
        map((appointments: IAppointment[]) => new fromActions.LoadAppointmentsSuccessForVin({appointments})),
        catchError(err => of(new fromActions.LoadAppointmentsFail({error: err})))
      )
    )
  ));
  
  cancelAppointment: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.CancelAppointmentFromBdcDashboard | fromActions.CancelAppointmentFromConfirm>(
      fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_FROM_BDCDASHBOARD,
      fromActions.BdcDashboardActionTypes.CANCEL_APPOINTMENT_FROM_CONFIRM
    ),
    mergeMap((action: fromActions.CancelAppointment) =>
      this.appointmentService.cancel(action.payload.appointment).pipe(
        map(() => new fromActions.CancelAppointmentSuccess({ appointmentId: action.payload.appointment.appointmentId })),
        catchError(err => of(new fromActions.CancelAppointmentFail({ error: err })))
      )
    )
  ));

  
  loadPendingAppointments: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadPendingAppointments>(
      fromActions.BdcDashboardActionTypes.LOAD_PENDING_APPOINTMENTS
    ),
    mergeMap((action: fromActions.LoadPendingAppointments) =>
      this.appointmentService.getPendingAppointments(action.payload.type, action.payload.offset, action.payload.limit, action.payload.sortingField, action.payload.sortOrder, false).pipe(
        switchMap((appointments: any) => fromAdapter.toAppointmentsProjector(appointments)),
        map((appointments: any) => {
          this.serviceAppointmentStore.dispatch(new LoadPendingAppointmentCount({type: action.payload.type, count: appointments.allAppointmentsCount}));
          return new fromActions.LoadPendingAppointmentsSuccess({ appointmentData: appointments, isLoad: true })
        }),
        catchError(err => {
          if (err.error?.status?.messages?.length) {
            this.alertService.showAlert(err.error?.status?.messages[0]);
          }
          else {
            this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
            return of(new fromActions.LoadPendingAppointmentsFailure({ error: err }));
          }
        })
      ))));

  
  cancelPendingAppointments: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.CancelPendingAppointments>(
      fromActions.BdcDashboardActionTypes.CANCEL_PENDING_APPOINTMENTS,
    ),
    mergeMap((action: fromActions.CancelPendingAppointments) =>
      this.appointmentService.cancel(action.payload.appointment).pipe(
        map(() => {
          this.serviceAppointmentStore.dispatch(new LoadPendingAppointmentCount({count: action.payload.count - 1, type: action.payload?.type}))
          this.alertService.showAlert(`Appointment cancelled successfully`, '', '', 5000, false);
          return new fromActions.CancelPendingAppointmentsSuccess({ appointmentId: action.payload.appointment.appointmentId })
        }),
        catchError(err => {
          if (err.error?.status?.messages?.length) {
            this.alertService.showAlert(err.error?.status?.messages[0]);
          }
          else {
            this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
            return of(new fromActions.CancelPendingAppointmentsFail({ error: err }));
          }
        }
        )
      )
    )
  ));

  
  sendLinkToCustomer: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.SendLinkToCustomer>(
      fromActions.BdcDashboardActionTypes.SEND_LINK_TO_CUSTOMER,
    ),
    mergeMap((action: fromActions.SendLinkToCustomer) =>
      this.appointmentService.create({ ...action.payload.appointment }).pipe(
        switchMap((appointment) => this.appointmentService.sendLink([appointment.data.appointmentId]).pipe(
          map((value) => {
            this.alertService.showAlert(`Link sent to customer successfully`, '', '', 5000, false);
            return new fromActions.SendLinkToCustomerSuccess({ appointmentId: action.payload.appointment.appointmentId })
          }),
          catchError(err => {
            if (err.error?.status?.messages?.length) {
              this.alertService.showAlert(err.error?.status?.messages[0]);
            }
            else {
              this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
            }
            return of(new fromActions.SendLinkToCustomerFailure({ error: err }));

          })
        )),
        catchError(err => {
          if (err.error?.status?.messages?.length) {
            this.alertService.showAlert(err.error?.status?.messages[0]);
          }
          else {
            this.alertService.showAlert(this.somethingWentWrongPleaseTryAgain);
          }
          return of(new fromActions.SendLinkToCustomerFailure({ error: err }))
        })
      )
    )
  ));

  
  loadSearchPendingAppointments: Observable<Action> = createEffect(() => this.actions$.pipe(
      ofType<fromActions.LoadSearchPendingAppointments>(
          fromActions.BdcDashboardActionTypes.LOAD_SEARCH_PENDING_APPOINTMENTS
      ),
      mergeMap((action: fromActions.LoadSearchPendingAppointments) =>
          this.appointmentService.getPendingAppointments('pending',action.payload.offset, action.payload.limit, action.payload.sortingField, action.payload.sortOrder, action.payload.isSearch, action.payload?.searchConfig).pipe(
              switchMap((appointments: IResponse<any>) => fromAdapter.toAppointmentsProjector(appointments)),
              map((appointmentData: any) => {
                 return new fromActions.LoadPendingAppointmentsSuccess({ appointmentData })
              }),
              catchError(err => {
              this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseTryAgain);
              return of(new fromActions.LoadPendingAppointmentsFailure({ error: err }))
      } )
          )
      )
  ));

}
